import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import * as moment from 'moment';
import { VariavelService } from './variavel.service';
import { ResumoEventoDiaService } from './resumo-evento.service';

export interface MarcacaoEventoLeitura {
  tipo_evento_id: number;
  icone: string;
  cor: string;
  descricao: string;
}

export interface LeituraEstacaoDiaResultado {
  dadosVariavel: any[];
  columnsVariavel: { field: string; header: string }[];
  descricaoEstacao: string;
  estacaoColetas: any[];
}

@Injectable({
  providedIn: 'root'
})
export class LeituraEstacaoDiaService {
  private static readonly TIPO_VARIAVEL_VAZAO = 4;
  private static readonly TIPO_VARIAVEL_CHUVA = 3;
  private static readonly TIPO_VARIAVEL_CHUVA_ACUMULADA = 5;
  /** Ordem fixa dos ícones em todas as linhas/colunas (mesma sequência sempre). */
  private static readonly ORDEM_TIPO_EVENTO = [1, 2, 3, 4, 5, 6, 9, 7, 8];
  private static readonly COLUNA_DATA = 'data';

  private tipoVariavel: { label: string; value: number }[] = [];

  constructor(
    private variavelService: VariavelService,
    private resumoEventoService: ResumoEventoDiaService,
  ) {}

  carregar(estacaoId: number, dataResumo: string): Observable<LeituraEstacaoDiaResultado> {
    const data = moment(dataResumo).format('YYYY-MM-DD');

    return this.garantirTiposVariavel().pipe(
      switchMap(() => {
        const params = {
          data_inicial: `${data}T00:00:00`,
          data_final: `${data}T23:59:59`,
          estacao_id: estacaoId,
          tipo_variavel_id: this.tipoVariavel.map(t => t.value),
        };

        return forkJoin([
          this.variavelService.group(params),
          this.variavelService.filter(params),
          this.resumoEventoService.ocorrenciasIrregulares(estacaoId, data),
        ]).pipe(
          map(([groupData, filterData, ocorrencias]) => {
            const resultado = this.montarTabela(groupData, filterData, data);
            this.aplicarMarcacoesEventos(resultado, ocorrencias || []);
            return resultado;
          })
        );
      })
    );
  }

  private garantirTiposVariavel(): Observable<void> {
    if (this.tipoVariavel.length > 0) {
      return of(void 0);
    }

    return this.variavelService.getType().pipe(
      map(json => {
        this.tipoVariavel = (json || []).map((item: any) => ({
          label: item.tipo_variavel,
          value: item.id,
        }));
        const ids = this.tipoVariavel.map(t => t.value);
        if (!ids.includes(LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA)) {
          this.tipoVariavel.push({ label: 'Chuva', value: LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA });
        }
        if (!ids.includes(LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA_ACUMULADA)) {
          this.tipoVariavel.push({ label: 'Chuva acumulada', value: LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA_ACUMULADA });
        }
      })
    );
  }

  private montarTabela(groupData: any, filterData: any, dataResumo: string): LeituraEstacaoDiaResultado {
    let estacaoColetas: any[] = [];
    let intervaloOperacao: number | null = null;
    let exibirVazao = false;
    let estacaoCasasDecimaisVazao: number | null = null;
    let dadosVariavel: any[] = [];
    let columnsVariavel: { field: string; header: string }[] = [];
    let descricaoEstacao = '';

    const groupArray = Array.isArray(groupData) ? groupData : [];
    const dadosGroupData = Array.isArray(filterData) ? filterData[0] : filterData;

    if (groupArray.length > 0 && groupArray[0]?.estacao) {
      estacaoColetas = groupArray[0].estacao.estacao_coletas || [];
      if (groupArray[0].estacao.exibir_vazao !== undefined) {
        exibirVazao = groupArray[0].estacao.exibir_vazao === true;
      }
      const casasVazao = groupArray[0].estacao.casas_decimais_vazao ?? groupArray[0].estacao.casasDecimaisVazao;
      if (casasVazao !== undefined && casasVazao !== null) {
        estacaoCasasDecimaisVazao = Number(casasVazao);
      }
    }

    if (dadosGroupData?.estacao) {
      if (!estacaoColetas.length && dadosGroupData.estacao.estacao_coletas) {
        estacaoColetas = dadosGroupData.estacao.estacao_coletas;
      }
      if (dadosGroupData.estacao.intervalo_operacao !== undefined && dadosGroupData.estacao.intervalo_operacao !== null) {
        intervaloOperacao = dadosGroupData.estacao.intervalo_operacao;
      }
      if (dadosGroupData.estacao.exibir_vazao !== undefined) {
        exibirVazao = dadosGroupData.estacao.exibir_vazao === true;
      }
      const casasVazaoEstacao = dadosGroupData.estacao.casas_decimais_vazao ?? dadosGroupData.estacao.casasDecimaisVazao;
      if (casasVazaoEstacao !== undefined && casasVazaoEstacao !== null) {
        estacaoCasasDecimaisVazao = Number(casasVazaoEstacao);
      }

      if (dadosGroupData.data_hora_dados && Array.isArray(dadosGroupData.data_hora_dados)) {
        dadosGroupData.data_hora_dados.forEach((dataItem: any) => {
          const dataHoraStr = dataItem.data_hora_dado.replace('Z', '');
          const dataHoraBrasilia = moment(dataHoraStr).utcOffset(-180, true);
          const groupedData: any = {
            data: dataHoraBrasilia.format('DD/MM/YYYY HH:mm'),
            timestamp: dataHoraBrasilia.valueOf(),
          };

          if (dataItem.variaveis && Array.isArray(dataItem.variaveis)) {
            const valoresPorTipo: Record<number, { value: any; name: string }> = {};
            dataItem.variaveis.forEach((variavelItem: any) => {
              if (variavelItem.dados && Array.isArray(variavelItem.dados)) {
                variavelItem.dados.forEach((dadoItem: any) => {
                  const estacaoColetaVariavel = estacaoColetas.find(x => x.tipo_variavel_id == dadoItem.tipo_variavel.id);
                  if (this.isVariavelVisivel(dadoItem.tipo_variavel.id, estacaoColetaVariavel, exibirVazao)) {
                    const value = this.formatarValor(dadoItem.valor, dadoItem.tipo_variavel.id, estacaoColetas, estacaoCasasDecimaisVazao);
                    valoresPorTipo[dadoItem.tipo_variavel.id] = {
                      value,
                      name: dadoItem.tipo_variavel.tipo_variavel,
                    };
                  }
                });
              }
            });

            Object.keys(valoresPorTipo).forEach(idStr => {
              const id = Number(idStr);
              let value = valoresPorTipo[id].value;
              if (this.deveInverterChuvaChuvaAcumulada(estacaoColetas)) {
                if (id === LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA && valoresPorTipo[LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA_ACUMULADA]) {
                  value = valoresPorTipo[LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA_ACUMULADA].value;
                } else if (id === LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA_ACUMULADA && valoresPorTipo[LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA]) {
                  value = valoresPorTipo[LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA].value;
                }
              }
              groupedData[valoresPorTipo[id].name] = value;
            });
          }

          dadosVariavel.push(groupedData);
        });
        dadosVariavel = this.sortByKey(dadosVariavel, 'timestamp', 'desc');
      }

      if (intervaloOperacao && intervaloOperacao > 0) {
        if (dadosVariavel.length === 0) {
          dadosVariavel = this.criarIntervalosVazios(intervaloOperacao, dataResumo, estacaoColetas, exibirVazao, groupArray);
        } else {
          dadosVariavel = this.preencherIntervalosTabela(dadosVariavel, intervaloOperacao, dataResumo, estacaoColetas, exibirVazao);
        }
      }

      if (dadosVariavel.length > 0) {
        const variavelNomeParaId: Record<string, number> = {};
        if (groupArray[0]?.tipos_variaveis) {
          groupArray[0].tipos_variaveis.forEach((variavel: any) => {
            if (variavel.tipo_variavel?.tipo_variavel && variavel.tipo_variavel?.id) {
              variavelNomeParaId[variavel.tipo_variavel.tipo_variavel] = variavel.tipo_variavel.id;
            }
          });
        }
        estacaoColetas.forEach((ec: any) => {
          if (!this.isFlagTrue(ec.visualizar) && !this.isFlagTrue(ec.visible)) {
            return;
          }
          const tipoVariavel = this.tipoVariavel.find(t => t.value == ec.tipo_variavel_id);
          const nome = tipoVariavel?.label
            || (ec.tipo_variavel_id === LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA_ACUMULADA ? 'Chuva acumulada'
              : ec.tipo_variavel_id === LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA ? 'Chuva' : null);
          if (nome && variavelNomeParaId[nome] === undefined) {
            variavelNomeParaId[nome] = ec.tipo_variavel_id;
          }
        });

        const allKeys = new Set<string>();
        dadosVariavel.forEach(record => {
          Object.keys(record).forEach(key => {
            if (key === 'data' || key === 'timestamp') {
              allKeys.add(key);
              return;
            }
            const tipoVariavelId = variavelNomeParaId[key];
            if (tipoVariavelId !== undefined) {
              const estacaoColetaVariavel = estacaoColetas.find(x => x.tipo_variavel_id == tipoVariavelId);
              if (this.isVariavelVisivel(tipoVariavelId, estacaoColetaVariavel, exibirVazao)) {
                allKeys.add(key);
              }
            }
          });
        });

        estacaoColetas.forEach((ec: any) => {
          if (!this.isFlagTrue(ec.visualizar) && !this.isFlagTrue(ec.visible)) {
            return;
          }
          const tipoVariavel = this.tipoVariavel.find(t => t.value == ec.tipo_variavel_id);
          const nome = tipoVariavel?.label
            || (ec.tipo_variavel_id === LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA_ACUMULADA ? 'Chuva acumulada'
              : ec.tipo_variavel_id === LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA ? 'Chuva' : null);
          if (nome) {
            allKeys.add(nome);
          }
        });

        columnsVariavel = Array.from(allKeys).map(key => ({ field: key, header: key.toUpperCase() }));
        const colunasVariavel = columnsVariavel
          .filter(c => c.field !== 'data' && c.field !== 'timestamp')
          .map(c => c.field);
        dadosVariavel.forEach(record => {
          colunasVariavel.forEach(col => {
            if (record[col] === undefined) {
              record[col] = null;
            }
          });
        });
      }

      descricaoEstacao = dadosGroupData.estacao.descricao || '';
    }

    return { dadosVariavel, columnsVariavel, descricaoEstacao, estacaoColetas };
  }

  private aplicarMarcacoesEventos(resultado: LeituraEstacaoDiaResultado, ocorrencias: any[]): void {
    const colunasVariavel = resultado.columnsVariavel
      .filter(c => c.field !== 'data' && c.field !== 'timestamp')
      .map(c => c.field);

    const colunaBateria = this.resolverColunaVariavel(colunasVariavel, ['BATERIA']);
    const colunaNivel = this.resolverColunaVariavel(colunasVariavel, ['NIVEL', 'NÍVEL']);
    const colunaChuva = this.resolverColunaVariavel(colunasVariavel, ['CHUVA']);

    const porMinutoColuna = new Map<number, Map<string, MarcacaoEventoLeitura[]>>();
    const eventosDataColuna: any[] = [];

    ocorrencias.forEach(ocorrencia => {
      const tipoEventoId = Number(ocorrencia.tipo_evento_id);
      const marcacao = this.criarMarcacao(ocorrencia);

      if (ocorrencia.modo === 'sem_coleta') {
        eventosDataColuna.push(ocorrencia);
        return;
      }

      if (tipoEventoId === 7 || tipoEventoId === 8) {
        if (!ocorrencia.data_hora_dado) {
          return;
        }
        const ts = this.arredondarParaMinuto(moment(ocorrencia.data_hora_dado).utcOffset(-180, true).valueOf());
        this.adicionarMarcacaoPorMinuto(porMinutoColuna, ts, LeituraEstacaoDiaService.COLUNA_DATA, marcacao);
        return;
      }

      const coluna = this.resolverColunaPorTipoEvento(tipoEventoId, colunaBateria, colunaNivel, colunaChuva);
      if (!coluna || !ocorrencia.data_hora_dado) {
        return;
      }

      const ts = this.arredondarParaMinuto(moment(ocorrencia.data_hora_dado).utcOffset(-180, true).valueOf());
      this.adicionarMarcacaoPorMinuto(porMinutoColuna, ts, coluna, marcacao);
    });

    resultado.dadosVariavel.forEach(linha => {
      const marcacoesPorColuna: Record<string, MarcacaoEventoLeitura[]> = {};
      const mapaLinha = porMinutoColuna.get(linha.timestamp);

      if (mapaLinha) {
        mapaLinha.forEach((marcacoes, coluna) => {
          marcacoesPorColuna[coluna] = this.ordenarMarcacoes(marcacoes);
        });
      }

      if (eventosDataColuna.length > 0 && this.linhaSemDados(linha, colunasVariavel)) {
        const colunaData = LeituraEstacaoDiaService.COLUNA_DATA;
        if (!marcacoesPorColuna[colunaData]) {
          marcacoesPorColuna[colunaData] = [];
        }
        eventosDataColuna.forEach(evento => {
          marcacoesPorColuna[colunaData].push(this.criarMarcacao(evento));
        });
        marcacoesPorColuna[colunaData] = this.ordenarMarcacoes(marcacoesPorColuna[colunaData]);
      }

      if (Object.keys(marcacoesPorColuna).length > 0) {
        linha.marcacoesPorColuna = marcacoesPorColuna;
      }
    });
  }

  private criarMarcacao(ocorrencia: any): MarcacaoEventoLeitura {
    return {
      tipo_evento_id: Number(ocorrencia.tipo_evento_id),
      icone: this.getIconeEvento(ocorrencia),
      cor: ocorrencia.cor || '#b0662f',
      descricao: ocorrencia.descricao || '',
    };
  }

  private adicionarMarcacaoPorMinuto(
    porMinutoColuna: Map<number, Map<string, MarcacaoEventoLeitura[]>>,
    timestamp: number,
    coluna: string,
    marcacao: MarcacaoEventoLeitura,
  ): void {
    if (!porMinutoColuna.has(timestamp)) {
      porMinutoColuna.set(timestamp, new Map());
    }
    const mapaColunas = porMinutoColuna.get(timestamp)!;
    if (!mapaColunas.has(coluna)) {
      mapaColunas.set(coluna, []);
    }
    const existentes = mapaColunas.get(coluna)!;
    if (!existentes.some(m => m.tipo_evento_id === marcacao.tipo_evento_id)) {
      existentes.push(marcacao);
    }
  }

  private resolverColunaPorTipoEvento(
    tipoEventoId: number,
    colunaBateria: string | null,
    colunaNivel: string | null,
    colunaChuva: string | null,
  ): string | null {
    if (tipoEventoId === 1 || tipoEventoId === 2) {
      return colunaBateria;
    }
    if (tipoEventoId === 3 || tipoEventoId === 4 || tipoEventoId === 5) {
      return colunaNivel;
    }
    if (tipoEventoId === 6 || tipoEventoId === 9) {
      return colunaChuva;
    }
    if (tipoEventoId === 7 || tipoEventoId === 8) {
      return LeituraEstacaoDiaService.COLUNA_DATA;
    }
    return null;
  }

  private resolverColunaVariavel(colunas: string[], termos: string[]): string | null {
    const normalizar = (texto: string) => texto
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .trim();

    for (const termo of termos) {
      const termoNorm = normalizar(termo);
      const encontrada = colunas.find(col => normalizar(col) === termoNorm);
      if (encontrada) {
        return encontrada;
      }
    }

    return null;
  }

  private ordenarMarcacoes(marcacoes: MarcacaoEventoLeitura[]): MarcacaoEventoLeitura[] {
    const ordem = (tipoEventoId: number) => {
      const idx = LeituraEstacaoDiaService.ORDEM_TIPO_EVENTO.indexOf(tipoEventoId);
      return idx === -1 ? 999 : idx;
    };
    return [...marcacoes].sort((a, b) => ordem(a.tipo_evento_id) - ordem(b.tipo_evento_id));
  }

  private linhaSemDados(linha: any, colunasVariavel: string[]): boolean {
    if (!colunasVariavel.length) {
      return true;
    }
    return colunasVariavel.every(col => linha[col] === null || linha[col] === undefined || linha[col] === '');
  }

  private getIconeEvento(row: any): string {
    if (Number(row?.tipo_evento_id) === 9) {
      return 'pi pi-exclamation-triangle';
    }
    return row?.icone || '';
  }

  private arredondarParaMinuto(ts: number): number {
    const m = moment(ts).utcOffset(-180);
    return m.clone().second(0).millisecond(0).valueOf();
  }

  private formatarValor(
    value: number | string,
    tipoVariavelId: number,
    estacaoColetas: any[],
    estacaoCasasDecimaisVazao: number | null,
  ): string {
    const numValue = typeof value === 'string' ? parseFloat(String(value).replace(',', '.')) : value;
    if (isNaN(numValue)) {
      return String(value);
    }

    let casasDecimais: number | null | undefined;
    if (tipoVariavelId === LeituraEstacaoDiaService.TIPO_VARIAVEL_VAZAO) {
      casasDecimais = estacaoCasasDecimaisVazao;
    }
    if (casasDecimais === null || casasDecimais === undefined) {
      const estacaoColetaVariavel = estacaoColetas.find(x => x.tipo_variavel_id == tipoVariavelId);
      casasDecimais = estacaoColetaVariavel?.casas_decimais;
    }
    if (casasDecimais === null || casasDecimais === undefined) {
      return numValue.toFixed(1).replace('.', ',');
    }
    return numValue.toFixed(casasDecimais).replace('.', ',');
  }

  private isFlagTrue(value: any): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private isVariavelVisivel(tipoVariavelId: number, estacaoColetaVariavel: any, exibirVazao: boolean): boolean {
    if (tipoVariavelId === LeituraEstacaoDiaService.TIPO_VARIAVEL_VAZAO && exibirVazao) {
      return true;
    }
    if (!estacaoColetaVariavel) {
      return false;
    }
    const isVisible = estacaoColetaVariavel.visible !== undefined
      ? this.isFlagTrue(estacaoColetaVariavel.visible)
      : this.isFlagTrue(estacaoColetaVariavel.visualizar);
    if (tipoVariavelId === LeituraEstacaoDiaService.TIPO_VARIAVEL_VAZAO) {
      return isVisible || exibirVazao;
    }
    return isVisible;
  }

  private deveInverterChuvaChuvaAcumulada(estacaoColetas: any[]): boolean {
    const chuvaAcumulada = estacaoColetas.find(
      x => x.tipo_variavel_id == LeituraEstacaoDiaService.TIPO_VARIAVEL_CHUVA_ACUMULADA
    );
    if (!chuvaAcumulada) {
      return false;
    }
    return this.isFlagTrue(chuvaAcumulada.visualizar) || this.isFlagTrue(chuvaAcumulada.visible);
  }

  private getDataFinalLimitadaIntervalos(dataResumo: string): moment.Moment {
    const dataAtualBrasilia = moment().utcOffset(-180);
    const dataFinal = moment(dataResumo).utcOffset(-180).endOf('day');
    const ultimoDiaFiltro = dataFinal.clone().startOf('day');
    const hoje = dataAtualBrasilia.clone().startOf('day');
    if (ultimoDiaFiltro.isBefore(hoje)) {
      return dataFinal;
    }
    return dataAtualBrasilia.clone();
  }

  private preencherIntervalosTabela(
    dadosVariavel: any[],
    intervaloOperacao: number,
    dataResumo: string,
    estacaoColetas: any[],
    exibirVazao: boolean,
  ): any[] {
    const dataFinalLimitada = this.getDataFinalLimitadaIntervalos(dataResumo);
    const variaveisKeys = new Set<string>();
    dadosVariavel.forEach(dado => {
      Object.keys(dado).forEach(key => {
        if (key !== 'data' && key !== 'timestamp') {
          variaveisKeys.add(key);
        }
      });
    });

    const dataInicial = moment(dataResumo).utcOffset(-180).startOf('day');
    const minutosArredondados = Math.floor(dataInicial.minute() / intervaloOperacao) * intervaloOperacao;
    const dataInicialAlinhada = dataInicial.clone().minute(minutosArredondados).second(0).millisecond(0);

    const intervalosAlinhados = new Set<number>();
    let dataAtual = dataInicialAlinhada.clone();
    while (dataAtual.isSameOrBefore(dataFinalLimitada)) {
      intervalosAlinhados.add(dataAtual.valueOf());
      dataAtual.add(intervaloOperacao, 'minutes');
    }

    const dadoPorMinuto = new Map<number, any>();
    dadosVariavel.forEach(dado => {
      const tsMinuto = this.arredondarParaMinuto(dado.timestamp);
      if (!dadoPorMinuto.has(tsMinuto)) {
        dadoPorMinuto.set(tsMinuto, dado);
      }
    });

    const resultado: any[] = [];
    intervalosAlinhados.forEach(timestamp => {
      const dado = dadoPorMinuto.get(timestamp);
      if (dado) {
        resultado.push({
          ...dado,
          data: moment(timestamp).utcOffset(-180).format('DD/MM/YYYY HH:mm'),
          timestamp,
        });
      } else {
        const novoRegistro: any = {
          data: moment(timestamp).utcOffset(-180).format('DD/MM/YYYY HH:mm'),
          timestamp,
        };
        variaveisKeys.forEach(key => { novoRegistro[key] = null; });
        resultado.push(novoRegistro);
      }
    });

    dadosVariavel.forEach(dado => {
      const tsMinuto = this.arredondarParaMinuto(dado.timestamp);
      if (!intervalosAlinhados.has(tsMinuto)) {
        resultado.push({
          ...dado,
          data: moment(dado.timestamp).utcOffset(-180).format('DD/MM/YYYY HH:mm'),
          timestamp: tsMinuto,
        });
      }
    });

    return this.sortByKey(resultado, 'timestamp', 'desc');
  }

  private criarIntervalosVazios(
    intervaloOperacao: number,
    dataResumo: string,
    estacaoColetas: any[],
    exibirVazao: boolean,
    groupArray: any[],
  ): any[] {
    const variaveisKeys = new Set<string>();
    if (groupArray[0]?.tipos_variaveis) {
      groupArray[0].tipos_variaveis.forEach((variavel: any) => {
        if (variavel.tipo_variavel?.tipo_variavel) {
          const estacaoColetaVariavel = estacaoColetas.find(x => x.tipo_variavel_id == variavel.tipo_variavel.id);
          if (this.isVariavelVisivel(variavel.tipo_variavel.id, estacaoColetaVariavel, exibirVazao)) {
            variaveisKeys.add(variavel.tipo_variavel.tipo_variavel);
          }
        }
      });
    }
    if (variaveisKeys.size === 0 && estacaoColetas.length > 0) {
      estacaoColetas.forEach(ec => {
        if (this.isVariavelVisivel(ec.tipo_variavel_id, ec, exibirVazao)) {
          const tipoVariavel = this.tipoVariavel.find(tv => tv.value == ec.tipo_variavel_id);
          if (tipoVariavel?.label) {
            variaveisKeys.add(tipoVariavel.label);
          }
        }
      });
    }

    const dataInicial = moment(dataResumo).utcOffset(-180).startOf('day');
    const dataFinalLimitada = this.getDataFinalLimitadaIntervalos(dataResumo);
    const minutosArredondados = Math.floor(dataInicial.minute() / intervaloOperacao) * intervaloOperacao;
    const dataInicialAlinhada = dataInicial.clone().minute(minutosArredondados).second(0).millisecond(0);

    const intervalosGerados: any[] = [];
    let dataAtual = dataInicialAlinhada.clone();
    while (dataAtual.isSameOrBefore(dataFinalLimitada)) {
      const timestamp = dataAtual.valueOf();
      const novoRegistro: any = {
        data: moment(timestamp).utcOffset(-180).format('DD/MM/YYYY HH:mm'),
        timestamp,
      };
      variaveisKeys.forEach(key => { novoRegistro[key] = null; });
      intervalosGerados.push(novoRegistro);
      dataAtual.add(intervaloOperacao, 'minutes');
    }

    return this.sortByKey(intervalosGerados, 'timestamp', 'desc');
  }

  private sortByKey(array: any[], key: string, type: 'asc' | 'desc' = 'asc'): any[] {
    return array.sort((a, b) => {
      const x = type === 'asc' ? a[key] : b[key];
      const y = type === 'asc' ? b[key] : a[key];
      return x < y ? -1 : x > y ? 1 : 0;
    });
  }
}
